import fp from 'lodash/fp';
import { pageLayouts, pageSizes } from '../constants';
import { noCapMap } from '../utils';
import { percentageChart, reportHeader, table } from './partials';
import { progressGoalCharts, shiftAndUnshift } from './partials/progress-goal-charts';

const chartOffset = { x: 110, y: 160 };
const tableOffset = { x: -110, y: 60 };

const riskColours = {
  low: '#1FD1A1',
  medium: '#FFC42B',
  high: '#FD3C64'
};

const mapRiskRateToColumn = (riskRate) => ({
  text: {
    colour: '#fff',
    value: riskRate
  },
  colour: riskRate === 'Low' ?
    riskColours.low :
    riskRate === 'Medium' ?
      riskColours.medium :
      riskColours.high
})

const totalUserCpdProgressReport = ({
  pageLayout,
  pageSize,
  reportee,
  afsl,
  minDate,
  maxDate,
  progressPercentage,
  // These are the non FASEA goals. Unlike the FASEA 'goal' variable below, 
  // these are structures, not just raw hours
  goals, 
  defaultProgressLabel,
  progressTableRows,
  goal // This is the FASEA goal in particular, in hours
}) => {
  /* 
   * (note, this comment has been added not by the original author)
   *
   * The structure of the PDF a "lists of lists" style format, where each "command" is a list
   * and the PDF is a list of commands. 
   * 
   * It seems also theres a kind of a "cursor" that can be moved by "translate" commands.
   */
  
  // In the case of a zero goal, there are some things we don't do need to do. 
  // Pass list as a function so we only need to evaluate it if necessary.
  const onlyKeepIfGoalNonZero = list => goal > 0 ? list() : []

  const shiftAndUnshiftIfGoalNonZero = list => 
    goal > 0 
      ? shiftAndUnshift(list)
      : list

  return [
    ...reportHeader({ report: 'Activity Report', reportee: `${reportee} CPD Progress`, afsl, minDate, maxDate }),
    ['fontSize', [24]],
    ['font', ['Helvetica']],
    ['fillColour', ['#1FD1A1']],
    ['translate', fp.values(chartOffset)],
    // Don't do the FASEA progress wheel if FASEA goal is zero
    ...onlyKeepIfGoalNonZero(() => percentageChart(progressPercentage)),
    ['fontSize', [12]],
    ['font', ['Helvetica']],
    ['fillColour', ['#666666']],
    // Also don't do the FASEA header text if FASEA goal is zero
    ...onlyKeepIfGoalNonZero(() => [['text', [`${defaultProgressLabel}`, -50, -70, {
      width: 100,
      align: 'center'
    }]]]),
    // If we do have a FASEA goal, we'll have to shift the cursor left before drawing progress wheels,
    // then back right after (so we don't overwrite the FASEA progress wheel). But if we have no FASEA goal
    // this is not necessary.
    ...shiftAndUnshiftIfGoalNonZero(progressGoalCharts(goals)),
    ['translate', fp.values(tableOffset)],
    ...table({
      pageLayout,
      pageSize: pageSize || 'A4',
      remainingPageHeight: fp.get(
        `${pageSize || 'A4'}.${pageLayout === pageLayouts.landscape ? 'x' : 'y'}`
      )(pageSizes) - (chartOffset.y + tableOffset.y),
      reportHeader: [...reportHeader({ report: 'Activity Report', reportee: `${reportee} CPD Progress`, afsl, minDate, maxDate }), ['translate', [0, tableOffset.y]],],
      headerRow: {
        colour: '#1fd1a1',
        text: {
          colour: '#fff'
        },
        columns: [
          {
            colSpan: 2,
            text: 'CPD category',
            bold: true
          },
          { text: 'Training Hrs Met' },
          { text: 'Training plan hrs' },
          { text: 'Completed hrs' },
          { text: 'Shortfall hrs' },
          { text: 'Total progress %' },
          { text: 'Shortfall %' },
          { text: 'Days left' },
          { text: 'Risk rating' }
        ]
      },
      rows: noCapMap(({
        cpdCategory,
        trainingHoursMet,
        trainingPlanHrs,
        totalProgress,
        totalProgressHrs,
        shortfall,
        shortfallHrs,
        daysLeft,
        tasaCpdHrs,
        riskRating,
        className
      }, index) => ({
        bold: className.includes('heavy'),
        colour: className.includes('normalCpdCategory') ?
          '#DDDDDD' :
          className.includes('parentCpdCategory') || className.includes('childCpdCategory') ?
            '#FFFFFF' :
            '#FBFAFA',
        columns: [
          {
            colSpan: 2,
            text: className.includes('childCpdCategory') ? `     ${cpdCategory}` : cpdCategory,
            bold: true
          },
          { text: `${trainingHoursMet}` },
          { text: `${trainingPlanHrs}` },
          { text: `${totalProgressHrs}` },
          { text: `${shortfallHrs}` },
          { text: totalProgress },
          { text: shortfall },
          { text: `${daysLeft}` },
          trainingPlanHrs > 0 ?
            mapRiskRateToColumn(riskRating) :
            className === 'normalCpdCategory' ?
              {
                text: {
                  colour: '#fff',
                  value: '-'
                },
                colour: riskColours.low
              } :
              { text: `` }
        ]
      }))(progressTableRows)
    })
  ];
}

export default totalUserCpdProgressReport;



// WEBPACK FOOTER //
// ./src/reports/total-user-cpd-progress.js